"use client";

import { useElementOnScreen } from "@/hooks/useElementOnScreen";
import { clsx } from "clsx";
import { useTranslations } from "next-intl";
import NextImage from "next/image";
import { useRef, useState } from "react";

interface TeamMember {
  name: string;
  role: string;
  color: string;
  image?: string;
  position?: string;
  zoom?: string;
  hasLegalContent?: boolean;
}

const teamMembers: TeamMember[] = [
  {
    name: "Géry Dupuis",
    role: "gery",
    color: "bg-sky-500",
    image: "/images/profiles/profile-gery.jpg",
  },
  {
    name: "Andréa Glaise",
    role: "andrea",
    color: "bg-rose-400",
    image: "/images/profiles/profile-andrea.jpg",
    position: "object-[center_top]",
    hasLegalContent: true,
  },
  {
    name: "Pierre Cozanet",
    role: "pierre",
    color: "bg-emerald-500",
    image: "/images/profiles/pierre-cozanet.jpg",
    position: "object-[center_10%]",
  },
];

const delayClasses = ["delay-100", "delay-200", "delay-300"];

function TeamCard({
  member,
  index,
  isVisible,
}: {
  member: TeamMember;
  index: number;
  isVisible: boolean;
}) {
  const t = useTranslations("team");
  const tLegal = useTranslations("specificLegalNoticePage");
  const [flipped, setFlipped] = useState(false);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const cardRef = useRef<HTMLDivElement>(null);

  const toggleFlip = () => setFlipped((v) => !v);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (flipped || !cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    const x = (px - 0.5) * 2;
    const y = (py - 0.5) * 2;
    setTilt({ x: -y * 15, y: x * 15 });
  };

  const resetTilt = () => setTilt({ x: 0, y: 0 });

  return (
    <div className="flex flex-col items-center text-center">
      <div
        className={clsx(
          "fade-in-up mb-6",
          isVisible && "visible",
          delayClasses[index],
        )}
      >
        <div
          ref={cardRef}
          role="button"
          tabIndex={0}
          aria-pressed={flipped}
          aria-label={
            flipped
              ? t("card.hide", { name: member.name })
              : t("card.show", { name: member.name })
          }
          onClick={toggleFlip}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleFlip();
            }
          }}
          onMouseMove={handleMouseMove}
          onMouseLeave={resetTilt}
          className={clsx(
            "team-card mx-auto cursor-pointer select-none",
            flipped && "is-flipped",
          )}
        >
          <div
            className="team-card-inner"
            style={{
              transform: flipped
                ? "rotateY(180deg)"
                : `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
            }}
          >
            <div
              className={clsx(
                "team-card-face team-card-face-front flex items-center justify-center text-white-cream text-5xl font-bold",
                !member.image && member.color,
              )}
            >
              {member.image ? (
                <NextImage
                  src={member.image}
                  alt={member.name}
                  fill
                  loading={index === 0 ? undefined : "lazy"}
                  className={clsx(
                    "object-cover",
                    member.position || "object-center",
                    member.zoom,
                  )}
                  sizes="(max-width: 640px) 288px, 320px"
                />
              ) : (
                member.name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
              )}
            </div>

            <div className="team-card-face team-card-face-back text-left">
              {member.hasLegalContent ? (
                <div className="space-y-2 text-[0.78rem] leading-snug">
                  <h4 className="font-serif text-sm font-bold text-forest-green leading-tight">
                    {tLegal("title")}
                  </h4>
                  <p className="font-semibold">{tLegal("lawyer.name")}</p>
                  <p>{tLegal("lawyer.bar")}</p>
                  <p>
                    {tLegal("lawyer.addressPrefix")}{" "}
                    {tLegal("lawyer.addressValue")}
                  </p>
                  <p>
                    <span className="font-semibold">
                      {tLegal("lawyer.contactLabel")}&nbsp;:
                    </span>{" "}
                    <a
                      href="mailto:contact@glaise-avocat.com"
                      onClick={(e) => e.stopPropagation()}
                      className="text-terracotta hover:text-terracotta-light transition-colors break-all"
                    >
                      contact@glaise-avocat.com
                    </a>
                  </p>
                  <p>{tLegal("content.p1")}</p>
                  <p>{tLegal("content.p2")}</p>
                  <p>{tLegal("content.p3")}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <h4 className="font-serif text-2xl text-forest-green">
                    {member.name}
                  </h4>
                  <p className="text-terracotta font-medium text-sm">
                    {t(`members.${member.role}.role`)}
                  </p>
                  <p className="text-sm text-charcoal/80 leading-relaxed">
                    {t(`members.${member.role}.bio`)}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <h3 className="text-xl font-serif mb-2">{member.name}</h3>
      <p className="text-terracotta-light">
        {t(`members.${member.role}.role`)}
      </p>
    </div>
  );
}

export function Team() {
  const { containerRef, isVisible } = useElementOnScreen();
  const t = useTranslations("team");

  return (
    <section
      id="equipe"
      ref={containerRef}
      className="py-24 bg-forest-green text-white-cream"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className={clsx("fade-in-up mb-16", isVisible && "visible")}>
          <h2 className="text-4xl md:text-5xl font-serif mb-4">{t("title")}</h2>
          <div className="line-accent bg-terracotta" />
        </div>

        <div className="grid md:grid-cols-3 gap-8 items-start">
          {teamMembers.map((member, index) => (
            <TeamCard
              key={member.name}
              member={member}
              index={index}
              isVisible={isVisible}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
